/**
 * Export/Import API Service
 *
 * Provides methods for exporting and importing entities in YAML format.
 */

import { request, apiErrorFromResponseBody } from './core'
import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'
import type { HydrationMode } from '../../constants/exportImport'

const { api: apiConfig, auth: authConfig } = config
const exportImportFetch = createServiceFetch(apiConfig.baseUrl)

// Base path for export-import endpoints (v1 versioned, not in legacy router)
const BASE_PATH = '/v1/export-import'

/**
 * Get auth headers for authenticated requests
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}

  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch {
    // Continue without token
  }

  // Get tenant ID
  const tenantId = localStorage.getItem('tagp-tenant-id')

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }

  return headers
}

/**
 * Helper to make authenticated GET fetch requests that return text (for YAML exports)
 */
async function authenticatedFetch(
  url: string,
  accept: string = 'text/yaml',
  tenantId?: string,
): Promise<string> {
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
    Accept: accept,
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId
  }

  const response = await exportImportFetch(`${apiConfig.baseUrl}${url}`, { headers })

  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    throw apiErrorFromResponseBody(
      response.status,
      errorText,
      `Export failed with status ${response.status}`
    )
  }

  return response.text()
}

/**
 * Helper to make authenticated POST fetch requests that return text (for bulk exports)
 */
async function authenticatedPostFetch(
  url: string,
  body: unknown,
  accept: string = 'text/yaml'
): Promise<string> {
  const headers: Record<string, string> = {
    ...getAuthHeaders(),
    'Content-Type': 'application/json',
    Accept: accept,
  }

  const response = await exportImportFetch(`${apiConfig.baseUrl}${url}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    throw apiErrorFromResponseBody(
      response.status,
      errorText,
      `Export failed with status ${response.status}`
    )
  }

  return response.text()
}

// ============================================================================
// DEPENDENCY TYPES
// ============================================================================

export interface AgentDependencyItem {
  id: string
  display_name: string
  entity_type: EntityType
  provider?: string
  model_id?: string
  type?: string
}

export interface AgentDependencies {
  agent: {
    id: string
    name: string
    description: string | null
  }
  dependencies: Partial<Record<string, AgentDependencyItem[]>>
}

// Types
export interface ImportRequest {
  project_id?: string // Optional - required for project-scoped entities (agents, tools, prompts, knowledge)
  yaml_content: string
  hydration_mode: HydrationMode
  dry_run?: boolean
  expected_entity_type?: string // Optional - if provided, import fails if YAML contains different entity type
}

export interface ImportEntityResult {
  portable_id: string
  entity_type: string
  action: 'created' | 'matched' | 'placeholder' | 'skipped' | 'failed'
  entity_id: string | null
  entity_name: string | null
  warnings: string[]
  errors: string[]
}

/**
 * Structured warning about missing or unconfigured dependencies.
 * Used to alert users that imported entities may not be fully functional
 * without additional configuration.
 */
export interface ConfigurationWarning {
  category: 'model' | 'prompt' | 'tool' | 'knowledge' | 'skill' | 'connector' | 'iam' | 'other'
  severity: 'critical' | 'warning' | 'info'
  message: string
  entity_name: string | null
  missing_reference: string | null
  action_required: string | null
}

export interface ImportResult {
  success: boolean
  dry_run: boolean
  entities: ImportEntityResult[]
  summary: Record<string, Record<string, number>>
  errors: string[]
  /** Warnings about missing configurations that may affect imported entity functionality */
  configuration_warnings: ConfigurationWarning[]
  /** True if there are critical configuration warnings that should be addressed */
  has_configuration_issues: boolean
}

export type EntityType =
  | 'model'
  | 'tool'
  | 'knowledge'
  | 'storage_folder'
  | 'connector'
  | 'iam'
  | 'agent_identity'
  | 'prompt'
  | 'skill'
  | 'agent'
  | 'blueprint'

/**
 * Export/Import API methods
 */
export const exportImportApi = {
  // ============================================================================
  // EXPORT ENDPOINTS
  // ============================================================================

  /**
   * Export a foundation model as YAML
   */
  exportModel: async (id: string, format: 'yaml' | 'json' = 'yaml', tenantId?: string): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/models/${id}/export?format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json',
      tenantId,
    )
  },

  /**
   * Export a tool as YAML
   */
  exportTool: async (id: string, format: 'yaml' | 'json' = 'yaml', tenantId?: string): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/tools/${id}/export?format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json',
      tenantId,
    )
  },

  /**
   * Export a knowledge source as YAML
   */
  exportKnowledge: async (
    id: string,
    includeConnector = false,
    format: 'yaml' | 'json' = 'yaml'
  ): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/knowledge/${id}/export?include_connector=${includeConnector}&format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  /**
   * Export a registered storage folder (bucket/path) as YAML
   */
  exportStorageFolder: async (
    id: string,
    includeConnector = false,
    format: 'yaml' | 'json' = 'yaml'
  ): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/storage-folders/${id}/export?include_connector=${includeConnector}&format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  /**
   * Export a connector as YAML
   */
  exportConnector: async (
    id: string,
    includeIam = false,
    format: 'yaml' | 'json' = 'yaml',
    tenantId?: string,
  ): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/connectors/${id}/export?include_iam=${includeIam}&format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json',
      tenantId,
    )
  },

  /**
   * Export an IAM config as YAML
   */
  exportIam: async (id: string, format: 'yaml' | 'json' = 'yaml'): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/iam/${id}/export?format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  /**
   * Export an Agent 365 identity as YAML
   */
  exportAgentIdentity: async (id: string, format: 'yaml' | 'json' = 'yaml'): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/agent-identities/${id}/export?format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  /**
   * Export a prompt as YAML
   */
  exportPrompt: async (
    id: string,
    includeVersions = true,
    format: 'yaml' | 'json' = 'yaml',
    tenantId?: string,
  ): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/prompts/${id}/export?include_versions=${includeVersions}&format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json',
      tenantId,
    )
  },

  /**
   * Export a skill as YAML
   */
  exportSkill: async (id: string, format: 'yaml' | 'json' = 'yaml', tenantId?: string): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/skills/${id}/export?format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json',
      tenantId,
    )
  },

  /**
   * Get the list of dependencies for an agent (for the export modal).
   */
  getAgentDependencies: async (agentId: string): Promise<AgentDependencies> => {
    const headers: Record<string, string> = {
      ...getAuthHeaders(),
      Accept: 'application/json',
    }
    const response = await exportImportFetch(
      `${apiConfig.baseUrl}${BASE_PATH}/agents/${agentId}/dependencies`,
      { headers }
    )
    if (!response.ok) {
      const errorText = await response.text().catch(() => '')
      throw apiErrorFromResponseBody(
        response.status,
        errorText,
        `Failed to fetch dependencies (status ${response.status})`
      )
    }
    return response.json()
  },

  /**
   * Export an agent as YAML
   */
  exportAgent: async (
    id: string,
    includeDependencies = true,
    format: 'yaml' | 'json' = 'yaml'
  ): Promise<string> => {
    return authenticatedFetch(
      `${BASE_PATH}/agents/${id}/export?include_dependencies=${includeDependencies}&format=${format}`,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  /**
   * Export an AI blueprint template as YAML (current snapshot, no dependencies)
   */
  exportBlueprint: async (id: string): Promise<string> => {
    return authenticatedFetch(`${BASE_PATH}/blueprints/${id}/export`, 'text/yaml')
  },

  /**
   * Export project bundle as YAML
   */
  exportProjectBundle: async (
    projectId: string,
    entityTypes?: string[],
    format: 'yaml' | 'json' = 'yaml'
  ): Promise<string> => {
    const params = new URLSearchParams({ format })
    if (entityTypes?.length) {
      params.set('entity_types', entityTypes.join(','))
    }
    return authenticatedFetch(
      `${BASE_PATH}/projects/${projectId}/export-bundle?${params}`,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  // ============================================================================
  // BULK EXPORT ENDPOINTS
  // ============================================================================

  /**
   * Export multiple models as YAML bundle
   */
  bulkExportModels: async (
    modelIds: string[],
    format: 'yaml' | 'json' = 'yaml'
  ): Promise<string> => {
    return authenticatedPostFetch(
      `${BASE_PATH}/bulk-export/models?format=${format}`,
      modelIds,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  /**
   * Export multiple tools as YAML bundle
   */
  bulkExportTools: async (toolIds: string[], format: 'yaml' | 'json' = 'yaml'): Promise<string> => {
    return authenticatedPostFetch(
      `${BASE_PATH}/bulk-export/tools?format=${format}`,
      toolIds,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  /**
   * Export multiple agents as YAML bundle
   */
  bulkExportAgents: async (
    agentIds: string[],
    includeDependencies = false,
    format: 'yaml' | 'json' = 'yaml'
  ): Promise<string> => {
    return authenticatedPostFetch(
      `${BASE_PATH}/bulk-export/agents?include_dependencies=${includeDependencies}&format=${format}`,
      agentIds,
      format === 'yaml' ? 'text/yaml' : 'application/json'
    )
  },

  // ============================================================================
  // IMPORT ENDPOINTS
  // ============================================================================

  /**
   * Import YAML content (single entity or bundle)
   */
  import: (data: ImportRequest): Promise<ImportResult> =>
    request<ImportResult>(`${BASE_PATH}/import`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Preview import (dry run)
   */
  preview: (data: ImportRequest): Promise<ImportResult> =>
    request<ImportResult>(`${BASE_PATH}/import/preview`, {
      method: 'POST',
      body: JSON.stringify({ ...data, dry_run: true }),
    }),

  /**
   * Import from file
   */
  importFile: async (
    file: File,
    projectId: string | undefined,
    hydrationMode: HydrationMode = 'match_or_create',
    dryRun = false,
    expectedEntityType?: string
  ): Promise<ImportResult> => {
    const formData = new FormData()
    formData.append('file', file)
    // Only append project_id if provided (required for project-scoped entities)
    if (projectId) {
      formData.append('project_id', projectId)
    }
    formData.append('hydration_mode', hydrationMode)
    formData.append('dry_run', String(dryRun))
    // Add expected entity type for server-side validation
    if (expectedEntityType) {
      formData.append('expected_entity_type', expectedEntityType)
    }

    const response = await exportImportFetch(`${apiConfig.baseUrl}${BASE_PATH}/import/file`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Import failed' }))
      throw new Error(error.detail || 'Import failed')
    }

    return response.json()
  },

  // ============================================================================
  // BLUEPRINT IMPORT ENDPOINTS (tenant-scoped; no project)
  // ============================================================================

  /**
   * Import a blueprint from an uploaded YAML file
   */
  importBlueprintFile: async (
    file: File,
    hydrationMode: HydrationMode = 'match_or_create',
    dryRun = false
  ): Promise<ImportResult> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('hydration_mode', hydrationMode)
    formData.append('dry_run', String(dryRun))

    const response = await exportImportFetch(
      `${apiConfig.baseUrl}${BASE_PATH}/blueprints/import/file`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Import failed' }))
      throw new Error(error.detail || 'Import failed')
    }

    return response.json()
  },
}

export default exportImportApi
